import { h, Component } from 'preact';
import { joinClasses } from 'utils/utils';
import { mod } from 'utils/math';

import Button from 'components/button/button';

import s from 'components/richInput/richInput.sss';

// Set to true to force the contenteditable div instead of input + OpaqueRange - if false, only used as a fallback if OpaqueRange is not available
const FORCE_CONTENTEDITABLE = false;

let highlight;
let activeContextHighlight;
if (!FORCE_CONTENTEDITABLE && typeof window !== 'undefined' && window.Highlight && 'highlights' in CSS && HTMLInputElement.prototype.createValueRange) {
	try {
		highlight = new window.Highlight();
		CSS.highlights.set('rich-input', highlight);
		activeContextHighlight = new window.Highlight();
		CSS.highlights.set('rich-input-selected-word', activeContextHighlight);
	} catch (err) {
		console.error('Failed to initialize highlight:', err);
	}
}

export default class RichInput extends Component {
	constructor(props) {
		super(props);

		this.state = {
			activeOptions: [],
			selectedOption: 0,
		};

		this.ranges = [];

		this.onBlur = this.onBlur.bind(this);
		this.onInput = this.onInput.bind(this);
		this.onKeyDown = this.onKeyDown.bind(this);
		this.onOptionClick = this.onOptionClick.bind(this);
		this.onSelectionChange = this.onSelectionChange.bind(this);
		this.fallbackOnBeforeInput = this.fallbackOnBeforeInput.bind(this);
		this.checkSuggestions = this.checkSuggestions.bind(this);
	}

	get value() {
		const input = this.getInput();
		if (!input) return '';
		return highlight ? input.value : input.textContent;
	}

	setValue(value) {
		const input = this.getInput();
		if (!input) return;
		if (highlight) {
			input.value = value;
		} else if (input.textContent !== value) {
			input.textContent = value;
			this.base.querySelector('input').value = value;
		}
		this.checkSuggestions();
		this.updateHighlight();
	}

	componentDidMount() {
		if (!highlight) {
			// The native input can listen to the event on the element itself, but the contenteditable fallback needs to listen globally
			document.addEventListener('selectionchange', this.onSelectionChange);
		}
		if (this.props.value) {
			this.setValue(this.props.value);
		}
	}

	componentWillReceiveProps(nextProps) {
		if (nextProps.value !== this.props.value) {
			this.setValue(nextProps.value);
		}
	}

	componentDidUpdate(prevProps) {
		if (this.props.value !== prevProps.value) {
			this.updateHighlight();
		}
	}

	componentWillUnmount() {
		document.removeEventListener('selectionchange', this.onSelectionChange);
		this.clearHighlight();
	}

	getInput() {
		return highlight ? this.base?.querySelector('input') : this.base?.querySelector(`.${s.content}`);
	}

	getContentEditableSelection(input = this.getInput()) {
		const selection = document.getSelection();

		if (!selection?.rangeCount || !input.contains(selection.anchorNode)) {
			return [];
		}

		const range = selection.getRangeAt(0);
		const measurer = range.cloneRange();

		measurer.selectNodeContents(input);
		measurer.setEnd(range.startContainer, range.startOffset);
		const selectionStart = measurer.toString().length;

		measurer.selectNodeContents(input);
		measurer.setEnd(range.endContainer, range.endOffset);
		const selectionEnd = measurer.toString().length;

		return [selectionStart, selectionEnd];
	}

	onBlur(e) {
		if (this.base.contains(e.relatedTarget)) return;

		// Still have to check for the actual focus to change, sometimes relatedTarget is null
		setTimeout(() => {
			if (this.base && !this.base.contains(document.activeElement)) {
				this.hideDropdown();
			}
		}, 0);
	}

	onInput(e) {
		this.props.onInput?.({ target: this, currentTarget: this });
		this.checkSuggestions();
		this.updateHighlight();
		if (!highlight) {
			this.base.querySelector('input').value = e.currentTarget.textContent;
		}
	}

	onKeyDown(e) {
		const state = this.state;
		if (this.isDropdownShown()) {
			switch (e.key) {
				case 'Enter':
				case 'Tab':
					e.preventDefault();
					this.useSuggestion(state.activeOptions?.[state.selectedOption]);
					return;
				case 'Escape':
					e.preventDefault();
					this.hideDropdown();
					return;
				case 'ArrowDown':
				case 'ArrowUp':
					e.preventDefault();
					const offset = e.key === 'ArrowDown' ? 1 : -1;
					this.setState({ selectedOption: mod(state.selectedOption + offset, state.activeOptions.length) });
					return;
			}
		}
		this.props.onKeyDown?.(e);
	}

	onOptionClick(e) {
		const button = e.currentTarget;
		const value = button?.value;
		this.useSuggestion(this.state.activeOptions.find(opt => opt.value === value));
	}

	onSelectionChange() {
		if (!highlight && document.activeElement === this.getInput()) {
			this.checkSuggestions();
		}
	}

	useSuggestion(suggestion) {
		if (!suggestion) return;
		const [from, to] = this.state.activeRange || [];
		const input = this.getInput();
		if (from == null || to == null || !input) return;
		const setSelectionTo = from + suggestion.value.length + 2;
		if (highlight) {
			const value = input.value;
			input.value = `${value.slice(0, from)}[${suggestion.value}]${value.slice(to)}`;
			input.setSelectionRange(setSelectionTo, setSelectionTo);
		} else {
			const value = input.textContent;
			input.textContent = `${value.slice(0, from)}[${suggestion.value}]${value.slice(to)}`;
			const range = document.createRange();
			range.setStart(input.firstChild, setSelectionTo);
			range.setEnd(input.firstChild, setSelectionTo);
			const selection = document.getSelection();
			selection?.removeAllRanges();
			selection?.addRange(range);
		}
		this.clearSuggestions();
		input.focus();
		input.dispatchEvent(new Event('input'));
	}

	getContext(value, at) {
		const contextBefore = value.slice(0, at).match(/\[([^\]\s]*)$/);
		if (!contextBefore) {
			const slashContext = value.slice(0, at).match(/\/([\w-]*)$/);
			if (slashContext) {
				const contextAfter = value.slice(at).match(/^([\w-]*)/);

				return {
					from: slashContext.index,
					to: at + (contextAfter?.[0].length || 0),
					textBeforeCursor: slashContext[1],
					textAfterCursor: contextAfter?.[1],
				};
			}
			return null;
		}
		const contextAfter = value.slice(at).match(/^([^\]\s]*)\]?/);

		const from = contextBefore.index;
		const to = at + (contextAfter?.[0].length || 0);

		return { from, to, textBeforeCursor: contextBefore[1], textAfterCursor: contextAfter?.[1] };
	}

	checkSuggestions() {
		const state = this.state;
		const input = this.getInput();
		if (!input?.matches(':focus-within')) return;

		const value = highlight ? input.value : input.textContent;
		const at = highlight ? input.selectionStart : this.getContentEditableSelection()[0];
		if (at == undefined) return this.clearSuggestions();

		const context = this.getContext(value, at);
		if (!context) return this.clearSuggestions();
		const { from, to, textBeforeCursor, textAfterCursor } = context;

		const search = new RegExp(RegExp.escape(textBeforeCursor) + '.*' + RegExp.escape(textAfterCursor || ''), 'i');
		const options = this.props.keywords?.filter(keyword => search.test(keyword.value)) || [];
		if (from === state.activeRange?.[0] && to === state.activeRange?.[1] && options.length === state.activeOptions.length && options.every((opt, i) => opt === state.activeOptions[i])) {
			// Suggestions have not changed, no need to update
			// But they might be hidden (if the input was blurred)
			if (options.length) this.showDropdown();
			return;
		}

		if (highlight) {
			if (this.activeContextRange) activeContextHighlight?.delete(this.activeContextRange);
			const range = input.createValueRange(from, to);
			this.activeContextRange = range;
			activeContextHighlight?.add(range);
		}

		this.setState({ activeOptions: options, selectedOption: 0, activeRange: [from, to] });
		if (options.length) {
			const position = this.getCaretPosition(input, from);
			this.showDropdown(position);
		} else {
			this.clearSuggestions();
		}
	}

	getCaretPosition(input, char) {
		if (highlight) {
			const range = input.createValueRange(0, char);
			return range.getBoundingClientRect().width;
		}
		const range = document.createRange();
		range.setStart(input, 0);
		range.setEnd(...this.getRangePositionFromOffset(input, char));
		return range.getBoundingClientRect().width;
	}

	showDropdown(position) {
		const dropdown = this.base.querySelector(`.${s.dropdown}`);
		if (position != undefined) dropdown.style.left = `${position}px`;
		dropdown?.togglePopover(true);
	}

	hideDropdown() {
		this.base.querySelector(`.${s.dropdown}`)?.togglePopover(false);
	}

	isDropdownShown() {
		return this.base.querySelector(`.${s.dropdown}`)?.matches(':popover-open') || false;
	}

	clearSuggestions() {
		if (this.state.activeOptions.length > 0) {
			this.setState({ activeOptions: [], selectedOption: 0 });
		}
		this.hideDropdown();
		if (this.activeContextRange) {
			activeContextHighlight?.delete(this.activeContextRange);
			this.activeContextRange = null;
		}
	}

	clearHighlight() {
		this.ranges.forEach(range => {
			range.disconnect();
			highlight?.delete(range);
		});
		this.ranges.length = 0;
	}

	getRangePositionFromOffset(input = this.getInput(), offset) {
		const walker = document.createTreeWalker(input, NodeFilter.SHOW_TEXT);
		let node;
		let traversed = 0;
		while ((node = walker.nextNode())) {
			if (traversed + node.length >= offset) {
				return [node, offset - traversed];
			}
			traversed += node.length;
		}
		return [input, input.childNodes.length];
	}

	updateHighlight() {
		try {
			const input = this.getInput();
			const keywords = this.props.keywords || [];
			const regex = new RegExp(
				keywords.map(keyword => RegExp.escape(`[${keyword.value}]`)).join('|'),
				'g'
			);

			if (highlight) {
				this.clearHighlight();
				if (!keywords.length) return;
				const value = input.value;
				let match;
				while ((match = regex.exec(value)) !== null) {
					const range = input.createValueRange(match.index, match.index + match[0].length);
					this.ranges.push(range);
					highlight?.add(range);
				}
				return;
			}

			const value = input.textContent;
			const [selectionStart, selectionEnd] = this.getContentEditableSelection(input);

			const fragment = document.createDocumentFragment();
			let previousIndex = 0;
			let match;
			while (keywords.length && (match = regex.exec(value))) {
				fragment.append(value.slice(previousIndex, match.index));
				const highlight = document.createElement('span');
				highlight.textContent = match[0];
				fragment.append(highlight);
				previousIndex = match.index + match[0].length;
			}
			fragment.append(value.slice(previousIndex));
			input.replaceChildren(fragment);

			if (selectionStart !== undefined) {
				const range = document.createRange();
				range.setStart(...this.getRangePositionFromOffset(input, selectionStart));
				range.setEnd(...this.getRangePositionFromOffset(input, selectionEnd));
				const selection = document.getSelection();
				selection.removeAllRanges();
				selection.addRange(range);
			}
		} catch (err) {
			console.error('Failed to update highlight:', err);
		}
	}

	fallbackOnBeforeInput(e) {
		// Avoid any newline
		if (this.props.readonly || e.inputType === 'insertLineBreak' || e.inputType === 'insertParagraph') {
			e.preventDefault();
		}
		if (e.data?.includes('\n')) {
			const range = document.getSelection()?.getRangeAt(0);
			if (range) {
				e.preventDefault();
				const toInsert = e.data.replace(/^\n+|\n+$/g, '').replace(/\n+/g, ' ');
				range.deleteContents();
				range.insertNode(document.createTextNode(toInsert));
				range.collapse();
				this.getInput()?.dispatchEvent(new Event('input'));
			}
		}
		this.props.onBeforeInput?.(e);
	}

	render(props, state) {
		const { class: className, keywords, value, ...passthrough } = props;
		return (
			<div class={joinClasses(s.container, className)} onFocusOut={this.onBlur}>
				<input {...passthrough} type={highlight ? 'text' : 'hidden'} onInput={this.onInput} onKeyDown={this.onKeyDown} onSelectionChange={this.checkSuggestions} />
				{!highlight && <Uncontrolled class={s.content} contenteditable="plaintext-only" onBeforeInput={this.fallbackOnBeforeInput} onInputCapture={this.onInput} onKeyDown={this.onKeyDown} onFocusIn={this.checkSuggestions} />}
				<div class={s.dropdown} popover="manual">
					<header>Keywords</header>
					{state.activeOptions.map((option, i) => (
						<Button type="button" class={joinClasses(i === state.selectedOption && s.active)} value={option.value} onClick={this.onOptionClick} tabIndex="-1">
							<div class={s.label}>{option.label}</div>
							<div class={s.value}>[{option.value}]</div>
						</Button>
					))}
				</div>
			</div>
		);
	}
}

class Uncontrolled extends Component {
	shouldComponentUpdate() {
		return false;
	}

	render(props) {
		return <div {...props} />;
	}
}